<script setup lang="ts">
import { Fragment } from '@tiptap/pm/model';
import type { Node as ProseMirrorNode, ResolvedPos } from '@tiptap/pm/model';
import { CellSelection } from '@tiptap/pm/tables';
import type { NodeViewProps } from '@tiptap/vue-3';
import { NodeViewContent, NodeViewWrapper } from '@tiptap/vue-3';
import {
    AlignCenter,
    AlignLeft,
    AlignRight,
    ArrowDown,
    ArrowUp,
    Bold,
    ChevronDown,
    ChevronLeft,
    Columns2,
    Copy,
    Eraser,
    GripVertical,
    GitMerge,
    Italic,
    MousePointer2,
    Palette,
    Rows3,
    Settings2,
    Trash2,
} from 'lucide-vue-next';
import { computed, inject, ref, watch } from 'vue';
import type { Ref } from 'vue';
import { Button } from '@/components/ui/button';
import {
    Command,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '@/components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';

const props = defineProps<NodeViewProps>();

// ── Live table counter ─────────────────────────────────────────────────
const tableCounters = inject<Readonly<{ value: ReadonlyMap<string, number> }>>(
    'editorTableCounters',
);

const tableNumber = computed(() => {
    const map = tableCounters?.value;
    const id = props.node.attrs.tableId as string | null;

    if (!map || !id) {
        return '?';
    }

    return map.get(id) ?? '?';
});

// ── Caption editing ────────────────────────────────────────────────────
const captionInput = ref<HTMLInputElement | null>(null);
const caption = computed(() => (props.node.attrs.caption as string) || '');

function updateCaption(e: Event) {
    props.updateAttributes({ caption: (e.target as HTMLInputElement).value });
}

// ── Commands ───────────────────────────────────────────────────────────
const { editor } = props;

const rowOpen = ref(false);
const colOpen = ref(false);
const formatOpen = ref(false);

function cmd(fn: () => void, close?: Ref<boolean>) {
    fn();

    if (close) {
        close.value = false;
    }
}

// Rows
const addRowBefore = () =>
    cmd(() => editor?.chain().focus().addRowBefore().run(), rowOpen);
const addRowAfter = () =>
    cmd(() => editor?.chain().focus().addRowAfter().run(), rowOpen);
const deleteRow = () =>
    cmd(() => editor?.chain().focus().deleteRow().run(), rowOpen);

// Columns
const addColumnBefore = () =>
    cmd(() => editor?.chain().focus().addColumnBefore().run(), colOpen);
const addColumnAfter = () =>
    cmd(() => editor?.chain().focus().addColumnAfter().run(), colOpen);
const deleteColumn = () =>
    cmd(() => editor?.chain().focus().deleteColumn().run(), colOpen);

// Merge / split (smart — merges if multiple selected, splits if merged cell)
const mergeOrSplit = () => editor?.chain().focus().mergeOrSplit().run();

// Headers
const toggleHeaderRow = () =>
    cmd(() => editor?.chain().focus().toggleHeaderRow().run(), formatOpen);
const toggleHeaderColumn = () =>
    cmd(() => editor?.chain().focus().toggleHeaderColumn().run(), formatOpen);

// Cell alignment
const setAlign = (align: string) =>
    cmd(
        () => editor?.chain().focus().setCellAttribute('align', align).run(),
        formatOpen,
    );

// Cell background
const cellColors = [
    {
        value: null,
        label: 'Défaut',
        cls: 'bg-white dark:bg-zinc-900 border border-border',
    },
    { value: 'gray', label: 'Gris', cls: 'bg-zinc-200 dark:bg-zinc-700' },
    { value: 'blue', label: 'Bleu', cls: 'bg-blue-100 dark:bg-blue-900' },
    { value: 'green', label: 'Vert', cls: 'bg-green-100 dark:bg-green-900' },
    { value: 'amber', label: 'Ambre', cls: 'bg-amber-100 dark:bg-amber-900' },
    { value: 'red', label: 'Rouge', cls: 'bg-red-100 dark:bg-red-900' },
    {
        value: 'purple',
        label: 'Violet',
        cls: 'bg-purple-100 dark:bg-purple-900',
    },
] as const;

const setCellBg = (value: string | null) =>
    cmd(
        () =>
            editor?.chain().focus().setCellAttribute('background', value).run(),
        formatOpen,
    );

const deleteTable = () => props.deleteNode();

// ── Row / column hover handles ─────────────────────────────────────────
const tableAreaRef = ref<HTMLElement | null>(null);
const tableContentRef = ref<HTMLElement | null>(null);

// Row handle
const rowHandleTop = ref<number | null>(null);
const hoveredRowEl = ref<HTMLTableRowElement | null>(null);
const rowMenuOpen = ref(false);
const rowColorMode = ref(false);

// Col handle
const colHandleLeft = ref<number | null>(null);
const hoveredColIndex = ref<number | null>(null);
const colMenuOpen = ref(false);
const colColorMode = ref(false);

watch(rowMenuOpen, (open) => {
    if (!open) {
        rowColorMode.value = false;
    }
});
watch(colMenuOpen, (open) => {
    if (!open) {
        colColorMode.value = false;
    }
});

// ── Leave delay — gives the mouse time to travel from the table edge to the handle ──
let leaveTimeout: ReturnType<typeof setTimeout> | null = null;

function scheduleLeave() {
    leaveTimeout = setTimeout(() => {
        if (rowMenuOpen.value || colMenuOpen.value) {
            return;
        }

        hoveredRowEl.value = null;
        hoveredColIndex.value = null;
        rowHandleTop.value = null;
        colHandleLeft.value = null;
    }, 200);
}

function cancelLeave() {
    if (leaveTimeout !== null) {
        clearTimeout(leaveTimeout);
        leaveTimeout = null;
    }
}

function onTableMove(e: MouseEvent) {
    cancelLeave();
    const areaEl = tableAreaRef.value;

    if (!areaEl) {
        return;
    }

    const areaRect = areaEl.getBoundingClientRect();
    const target = e.target as HTMLElement;

    if (!rowMenuOpen.value) {
        const tr = target.closest('tr') as HTMLTableRowElement | null;

        if (tr) {
            hoveredRowEl.value = tr;
            const trRect = tr.getBoundingClientRect();
            rowHandleTop.value = trRect.top - areaRect.top + trRect.height / 2;
        }
    }

    if (!colMenuOpen.value) {
        const cell = target.closest('td, th') as HTMLTableCellElement | null;

        if (cell) {
            hoveredColIndex.value = cell.cellIndex;
            const cellRect = cell.getBoundingClientRect();
            colHandleLeft.value =
                cellRect.left - areaRect.left + cellRect.width / 2;

            // Track cursor Y within the cell so the resize-handle pill follows the cursor
            const yInCell = e.clientY - cellRect.top;
            const yPct = Math.max(
                0,
                Math.min(100, (yInCell / cellRect.height) * 100),
            );
            tableContentRef.value?.style.setProperty('--resize-y', yPct + '%');
        }
    }
}

function onTableLeave() {
    scheduleLeave();
}

// ── Find row/col node helpers ──────────────────────────────────────────
function findRowNodeAt(cellEl: HTMLElement) {
    if (!editor) {
        return null;
    }

    try {
        const domPos = editor.view.posAtDOM(cellEl, 0);
        const $pos = editor.state.doc.resolve(domPos);

        for (let d = $pos.depth; d >= 0; d--) {
            if ($pos.node(d).type.name === 'tableRow') {
                return { node: $pos.node(d), pos: $pos.before(d) };
            }
        }
    } catch {
        /* skip */
    }

    return null;
}

function findCellNodeAt(cellEl: HTMLElement) {
    if (!editor) {
        return null;
    }

    try {
        const domPos = editor.view.posAtDOM(cellEl, 0);
        const $pos = editor.state.doc.resolve(domPos);

        for (let d = $pos.depth; d >= 0; d--) {
            const name = $pos.node(d).type.name;

            if (name === 'tableCell' || name === 'tableHeader') {
                return { node: $pos.node(d), pos: $pos.before(d) };
            }
        }
    } catch {
        /* skip */
    }

    return null;
}

// ── CellSelection helpers ──────────────────────────────────────────────
// Returns the resolved position OF a cell node (not inside its content).
// ProseMirror's CellSelection requires positions that resolve to inside cells
// but via the cellAround logic (position before the cell at row depth + 1).
function resolvedCellPos(cellEl: HTMLElement): ResolvedPos | null {
    if (!editor) {
        return null;
    }

    try {
        const domPos = editor.view.posAtDOM(cellEl, 0);
        const $pos = editor.state.doc.resolve(domPos);

        for (let d = $pos.depth - 1; d > 0; d--) {
            if (
                ($pos.node(d).type.spec as Record<string, unknown>)[
                    'tableRole'
                ] === 'row'
            ) {
                return editor.state.doc.resolve($pos.before(d + 1));
            }
        }
    } catch {
        /* skip */
    }

    return null;
}

// Selects all cells in the hovered row using a ProseMirror CellSelection.
function rowSelect() {
    const tr = hoveredRowEl.value;

    if (!editor || !tr) {
        return;
    }

    const cells = Array.from(tr.querySelectorAll('td, th')) as HTMLElement[];

    if (cells.length === 0) {
        return;
    }

    const $anchor = resolvedCellPos(cells[0]);
    const $head = resolvedCellPos(cells[cells.length - 1]);

    if (!$anchor || !$head) {
        return;
    }

    try {
        const sel = CellSelection.rowSelection($anchor, $head);
        editor.view.dispatch(editor.state.tr.setSelection(sel));
        editor.view.focus();
    } catch {
        /* skip */
    }
}

// Selects all cells in the hovered column using a ProseMirror CellSelection.
function colSelect() {
    const colIdx = hoveredColIndex.value;

    if (!editor || colIdx === null) {
        return;
    }

    const table = tableContentRef.value?.querySelector('table');

    if (!table) {
        return;
    }

    const rows = Array.from(
        table.querySelectorAll('tr'),
    ) as HTMLTableRowElement[];

    if (rows.length === 0) {
        return;
    }

    const firstCell = rows[0].cells[colIdx] as HTMLElement | undefined;
    const lastCell = rows[rows.length - 1].cells[colIdx] as
        | HTMLElement
        | undefined;

    if (!firstCell || !lastCell) {
        return;
    }

    const $anchor = resolvedCellPos(firstCell);
    const $head = resolvedCellPos(lastCell);

    if (!$anchor || !$head) {
        return;
    }

    try {
        const sel = CellSelection.colSelection($anchor, $head);
        editor.view.dispatch(editor.state.tr.setSelection(sel));
        editor.view.focus();
    } catch {
        /* skip */
    }
}

// Selects the hovered row/col then applies a mark to all its text content.
function rowApplyMark(mark: 'bold' | 'italic') {
    rowSelect();

    if (mark === 'bold') {
        editor?.chain().focus().toggleBold().run();
    } else {
        editor?.chain().focus().toggleItalic().run();
    }

    rowMenuOpen.value = false;
}
function colApplyMark(mark: 'bold' | 'italic') {
    colSelect();

    if (mark === 'bold') {
        editor?.chain().focus().toggleBold().run();
    } else {
        editor?.chain().focus().toggleItalic().run();
    }

    colMenuOpen.value = false;
}

// ── Row handle click — select entire row then let Popover open ─────────
function onRowHandleClick() {
    rowSelect();
    rowColorMode.value = false;
}

// ── Row menu actions ───────────────────────────────────────────────────
function rowInsertBefore() {
    editor?.chain().focus().addRowBefore().run();
    rowMenuOpen.value = false;
}
function rowInsertAfter() {
    editor?.chain().focus().addRowAfter().run();
    rowMenuOpen.value = false;
}
function rowDuplicate() {
    const tr = hoveredRowEl.value;

    if (!editor || !tr) {
        rowMenuOpen.value = false;

        return;
    }

    const cell = tr.querySelector('td, th') as HTMLElement | null;

    if (!cell) {
        rowMenuOpen.value = false;

        return;
    }

    const found = findRowNodeAt(cell);

    if (found) {
        const pmTr = editor.state.tr;
        pmTr.insert(
            found.pos + found.node.nodeSize,
            found.node.copy(found.node.content),
        );
        editor.view.dispatch(pmTr);
    } else {
        editor.chain().focus().addRowAfter().run();
    }

    rowMenuOpen.value = false;
}
function rowClearContents() {
    const tr = hoveredRowEl.value;

    if (!editor || !tr) {
        rowMenuOpen.value = false;

        return;
    }

    const cell = tr.querySelector('td, th') as HTMLElement | null;

    if (!cell) {
        rowMenuOpen.value = false;

        return;
    }

    const found = findRowNodeAt(cell);

    if (found) {
        const emptyCell = (node: ProseMirrorNode) => {
            const emptyParagraph = editor.schema.nodes.paragraph.create();

            return node.copy(Fragment.from(emptyParagraph));
        };
        const newCells = found.node.content.content.map(emptyCell);
        const newRow = found.node.copy(Fragment.from(newCells));
        const pmTr = editor.state.tr;
        pmTr.replaceWith(found.pos, found.pos + found.node.nodeSize, newRow);
        editor.view.dispatch(pmTr);
    }

    rowMenuOpen.value = false;
}
function rowDelete() {
    editor?.chain().focus().deleteRow().run();
    rowMenuOpen.value = false;
    onTableLeave();
}
function rowSetColor(color: string | null) {
    const tr = hoveredRowEl.value;

    if (!editor || !tr) {
        rowMenuOpen.value = false;

        return;
    }

    const cells = Array.from(tr.querySelectorAll('td, th')) as HTMLElement[];
    const pmTr = editor.state.tr;
    cells.forEach((cellEl) => {
        const found = findCellNodeAt(cellEl);

        if (found) {
            pmTr.setNodeMarkup(found.pos, undefined, {
                ...found.node.attrs,
                background: color,
            });
        }
    });
    editor.view.dispatch(pmTr);
    rowMenuOpen.value = false;
    rowColorMode.value = false;
}

// ── Col handle click — select entire column then let Popover open ──────
function onColHandleClick() {
    colSelect();
    colColorMode.value = false;
}

// ── Col menu actions ───────────────────────────────────────────────────
function colInsertBefore() {
    editor?.chain().focus().addColumnBefore().run();
    colMenuOpen.value = false;
}
function colInsertAfter() {
    editor?.chain().focus().addColumnAfter().run();
    colMenuOpen.value = false;
}
function colDuplicate() {
    const colIdx = hoveredColIndex.value;

    if (!editor || colIdx === null) {
        colMenuOpen.value = false;

        return;
    }

    // TipTap doesn't have a built-in duplicate column; fall back to addColumnAfter
    editor.chain().focus().addColumnAfter().run();
    colMenuOpen.value = false;
}
function colClearContents() {
    const colIdx = hoveredColIndex.value;

    if (!editor || colIdx === null) {
        colMenuOpen.value = false;

        return;
    }

    const table = tableContentRef.value?.querySelector('table');

    if (!table) {
        colMenuOpen.value = false;

        return;
    }

    const rows = Array.from(
        table.querySelectorAll('tr'),
    ) as HTMLTableRowElement[];
    // Collect cell info then reverse so we apply from the end of the doc backwards
    // (higher positions first), preventing position shifts from affecting earlier cells.
    const cellInfos = rows
        .map((row) => {
            const cellEl = row.cells[colIdx] as HTMLElement | undefined;

            return cellEl ? findCellNodeAt(cellEl) : null;
        })
        .filter(Boolean)
        .reverse();
    const pmTr = editor.state.tr;
    cellInfos.forEach((found) => {
        if (!found) {
            return;
        }

        const emptyParagraph = editor.schema.nodes.paragraph.create();
        const newCell = found.node.copy(Fragment.from(emptyParagraph));
        pmTr.replaceWith(found.pos, found.pos + found.node.nodeSize, newCell);
    });
    editor.view.dispatch(pmTr);
    colMenuOpen.value = false;
}
function colDelete() {
    editor?.chain().focus().deleteColumn().run();
    colMenuOpen.value = false;
    onTableLeave();
}
function colSetColor(color: string | null) {
    const colIdx = hoveredColIndex.value;

    if (!editor || colIdx === null) {
        colMenuOpen.value = false;

        return;
    }

    const table = tableContentRef.value?.querySelector('table');

    if (!table) {
        colMenuOpen.value = false;

        return;
    }

    const rows = Array.from(
        table.querySelectorAll('tr'),
    ) as HTMLTableRowElement[];
    const pmTr = editor.state.tr;
    rows.forEach((row) => {
        const cellEl = row.cells[colIdx] as HTMLElement | undefined;

        if (!cellEl) {
            return;
        }

        const found = findCellNodeAt(cellEl);

        if (found) {
            pmTr.setNodeMarkup(found.pos, undefined, {
                ...found.node.attrs,
                background: color,
            });
        }
    });
    editor.view.dispatch(pmTr);
    colMenuOpen.value = false;
    colColorMode.value = false;
}
</script>

<template>
    <NodeViewWrapper class="numbered-table-wrapper my-6">
        <!-- Read-only: just the caption text, no editing toolbar -->
        <p
            v-if="!props.editor?.isEditable"
            class="mb-1.5 text-sm text-muted-foreground"
        >
            {{ caption }}
        </p>

        <!-- ── Toolbar ─────────────────────────────────────────────── -->
        <div v-else class="mb-1.5 flex items-center gap-1">
            <!-- Lignes -->
            <Popover v-model:open="rowOpen">
                <PopoverTrigger as-child>
                    <Button
                        variant="ghost"
                        size="sm"
                        class="h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
                    >
                        <Rows3 class="h-3.5 w-3.5" />
                        Lignes
                        <ChevronDown class="h-3 w-3 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent align="start" class="w-44 p-1">
                    <button class="table-menu-item" @click="addRowBefore">
                        <Rows3
                            class="h-3.5 w-3.5 text-muted-foreground"
                        />Insérer avant
                    </button>
                    <button class="table-menu-item" @click="addRowAfter">
                        <Rows3
                            class="h-3.5 w-3.5 text-muted-foreground"
                        />Insérer après
                    </button>
                    <div class="my-1 h-px bg-border/60" />
                    <button
                        class="table-menu-item text-destructive hover:!bg-destructive/10"
                        @click="deleteRow"
                    >
                        <Rows3 class="h-3.5 w-3.5" />Supprimer
                    </button>
                </PopoverContent>
            </Popover>

            <!-- Colonnes -->
            <Popover v-model:open="colOpen">
                <PopoverTrigger as-child>
                    <Button
                        variant="ghost"
                        size="sm"
                        class="h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
                    >
                        <Columns2 class="h-3.5 w-3.5" />
                        Colonnes
                        <ChevronDown class="h-3 w-3 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent align="start" class="w-44 p-1">
                    <button class="table-menu-item" @click="addColumnBefore">
                        <Columns2
                            class="h-3.5 w-3.5 text-muted-foreground"
                        />Insérer avant
                    </button>
                    <button class="table-menu-item" @click="addColumnAfter">
                        <Columns2
                            class="h-3.5 w-3.5 text-muted-foreground"
                        />Insérer après
                    </button>
                    <div class="my-1 h-px bg-border/60" />
                    <button
                        class="table-menu-item text-destructive hover:!bg-destructive/10"
                        @click="deleteColumn"
                    >
                        <Columns2 class="h-3.5 w-3.5" />Supprimer
                    </button>
                </PopoverContent>
            </Popover>

            <div class="h-4 w-px bg-border/50" />

            <!-- Caption -->
            <input
                ref="captionInput"
                type="text"
                :value="caption"
                placeholder="Ajouter une légende…"
                class="min-w-0 flex-1 bg-transparent text-sm text-muted-foreground placeholder:text-muted-foreground/40 focus:outline-none"
                @input="updateCaption"
            />

            <!-- Fusionner / diviser (smart) -->
            <Button
                variant="ghost"
                size="icon"
                class="h-7 w-7 text-muted-foreground hover:text-foreground"
                title="Fusionner / diviser les cellules"
                @click="mergeOrSplit"
            >
                <GitMerge class="h-3.5 w-3.5" />
            </Button>

            <!-- Format -->
            <Popover v-model:open="formatOpen">
                <PopoverTrigger as-child>
                    <Button
                        variant="ghost"
                        size="icon"
                        class="h-7 w-7 text-muted-foreground hover:text-foreground"
                        title="Format du tableau"
                    >
                        <Settings2 class="h-3.5 w-3.5" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent align="end" class="w-52 p-3">
                    <!-- En-têtes -->
                    <p
                        class="mb-2 text-[10px] font-semibold tracking-wider text-muted-foreground uppercase"
                    >
                        En-têtes
                    </p>
                    <div class="mb-3 flex gap-2">
                        <button
                            class="flex flex-1 flex-col items-center gap-1 rounded border border-border/60 py-2 text-[10px] font-medium text-muted-foreground transition-colors hover:border-sidebar-primary hover:text-foreground"
                            @click="toggleHeaderRow"
                        >
                            <Rows3 class="h-3.5 w-3.5" />Ligne
                        </button>
                        <button
                            class="flex flex-1 flex-col items-center gap-1 rounded border border-border/60 py-2 text-[10px] font-medium text-muted-foreground transition-colors hover:border-sidebar-primary hover:text-foreground"
                            @click="toggleHeaderColumn"
                        >
                            <Columns2 class="h-3.5 w-3.5" />Colonne
                        </button>
                    </div>

                    <div class="mb-3 h-px bg-border/60" />

                    <!-- Alignement -->
                    <p
                        class="mb-2 text-[10px] font-semibold tracking-wider text-muted-foreground uppercase"
                    >
                        Alignement cellule
                    </p>
                    <div class="mb-3 flex gap-1">
                        <button
                            class="flex flex-1 items-center justify-center rounded border border-border/60 p-1.5 text-muted-foreground transition-colors hover:border-sidebar-primary hover:text-foreground"
                            title="Gauche"
                            @click="setAlign('left')"
                        >
                            <AlignLeft class="h-3.5 w-3.5" />
                        </button>
                        <button
                            class="flex flex-1 items-center justify-center rounded border border-border/60 p-1.5 text-muted-foreground transition-colors hover:border-sidebar-primary hover:text-foreground"
                            title="Centré"
                            @click="setAlign('center')"
                        >
                            <AlignCenter class="h-3.5 w-3.5" />
                        </button>
                        <button
                            class="flex flex-1 items-center justify-center rounded border border-border/60 p-1.5 text-muted-foreground transition-colors hover:border-sidebar-primary hover:text-foreground"
                            title="Droite"
                            @click="setAlign('right')"
                        >
                            <AlignRight class="h-3.5 w-3.5" />
                        </button>
                    </div>

                    <div class="mb-3 h-px bg-border/60" />

                    <!-- Couleur de fond -->
                    <p
                        class="mb-2 text-[10px] font-semibold tracking-wider text-muted-foreground uppercase"
                    >
                        Couleur de fond
                    </p>
                    <div class="flex flex-wrap gap-2">
                        <button
                            v-for="c in cellColors"
                            :key="c.value ?? 'none'"
                            :class="[
                                'h-6 w-6 rounded-full border-2 border-transparent transition-all hover:scale-110 hover:border-foreground/40',
                                c.cls,
                            ]"
                            :title="c.label"
                            @click="setCellBg(c.value)"
                        />
                    </div>
                </PopoverContent>
            </Popover>

            <!-- Supprimer tableau -->
            <Button
                variant="ghost"
                size="icon"
                class="h-7 w-7 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                title="Supprimer le tableau"
                @click="deleteTable"
            >
                <Trash2 class="h-3.5 w-3.5" />
            </Button>
        </div>

        <!-- ── Table container ───────────────────────────────────────── -->
        <div
            ref="tableAreaRef"
            class="relative mt-5"
            @mousemove="onTableMove"
            @mouseleave="onTableLeave"
        >
            <!-- Row grip handle — vertically centred on the hovered row -->
            <div
                v-if="rowHandleTop !== null || rowMenuOpen"
                class="absolute z-30 -translate-y-1/2"
                :style="{ top: (rowHandleTop ?? 0) + 'px', left: '-28px' }"
                @mouseenter="cancelLeave"
            >
                <Popover v-model:open="rowMenuOpen">
                    <PopoverTrigger as-child>
                        <button
                            class="flex h-6 w-6 items-center justify-center rounded text-muted-foreground/50 transition-colors hover:bg-muted hover:text-foreground"
                            title="Options de la ligne"
                            @click="onRowHandleClick"
                        >
                            <GripVertical class="h-3.5 w-3.5" />
                        </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" side="right" class="w-56 p-0">
                        <Command>
                            <CommandInput
                                placeholder="Rechercher des actions…"
                            />
                            <CommandList>
                                <!-- Color mode -->
                                <template v-if="rowColorMode">
                                    <div
                                        class="flex items-center gap-1 border-b border-border/40 px-2 py-1.5"
                                    >
                                        <button
                                            class="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                                            @click="rowColorMode = false"
                                        >
                                            <ChevronLeft
                                                class="h-3 w-3"
                                            />Retour
                                        </button>
                                    </div>
                                    <div class="px-3 py-2">
                                        <p
                                            class="mb-2 text-[10px] font-semibold tracking-wider text-muted-foreground uppercase"
                                        >
                                            Couleur de fond
                                        </p>
                                        <div class="flex flex-wrap gap-2">
                                            <button
                                                v-for="c in cellColors"
                                                :key="c.value ?? 'none'"
                                                :class="[
                                                    'h-6 w-6 rounded-full border-2 border-transparent transition-all hover:scale-110 hover:border-foreground/40',
                                                    c.cls,
                                                ]"
                                                :title="c.label"
                                                @click="rowSetColor(c.value)"
                                            />
                                        </div>
                                    </div>
                                </template>

                                <!-- Main menu -->
                                <template v-else>
                                    <CommandGroup>
                                        <CommandItem
                                            value="selectionner-ligne"
                                            @select="rowSelect"
                                        >
                                            <MousePointer2 class="h-4 w-4" />
                                            <span>Sélectionner la ligne</span>
                                        </CommandItem>
                                    </CommandGroup>
                                    <CommandSeparator />
                                    <CommandGroup>
                                        <CommandItem
                                            value="gras-ligne"
                                            @select="() => rowApplyMark('bold')"
                                        >
                                            <Bold class="h-4 w-4" />
                                            <span>Gras</span>
                                        </CommandItem>
                                        <CommandItem
                                            value="italique-ligne"
                                            @select="
                                                () => rowApplyMark('italic')
                                            "
                                        >
                                            <Italic class="h-4 w-4" />
                                            <span>Italique</span>
                                        </CommandItem>
                                    </CommandGroup>
                                    <CommandSeparator />
                                    <CommandGroup>
                                        <CommandItem
                                            value="couleur"
                                            @select.prevent="
                                                rowColorMode = true
                                            "
                                        >
                                            <Palette class="h-4 w-4" />
                                            <span>Couleur</span>
                                            <ChevronLeft
                                                class="ml-auto h-3 w-3 rotate-180 text-muted-foreground"
                                            />
                                        </CommandItem>
                                    </CommandGroup>
                                    <CommandSeparator />
                                    <CommandGroup>
                                        <CommandItem
                                            value="inserer-au-dessus"
                                            @select="rowInsertBefore"
                                        >
                                            <ArrowUp class="h-4 w-4" />
                                            <span>Insérer au-dessus</span>
                                        </CommandItem>
                                        <CommandItem
                                            value="inserer-en-dessous"
                                            @select="rowInsertAfter"
                                        >
                                            <ArrowDown class="h-4 w-4" />
                                            <span>Insérer en dessous</span>
                                        </CommandItem>
                                        <CommandItem
                                            value="dupliquer"
                                            @select="rowDuplicate"
                                        >
                                            <Copy class="h-4 w-4" />
                                            <span>Dupliquer</span>
                                        </CommandItem>
                                    </CommandGroup>
                                    <CommandSeparator />
                                    <CommandGroup>
                                        <CommandItem
                                            value="effacer"
                                            @select="rowClearContents"
                                        >
                                            <Eraser class="h-4 w-4" />
                                            <span>Effacer les contenus</span>
                                        </CommandItem>
                                        <CommandItem
                                            value="supprimer-ligne"
                                            class="text-destructive data-[highlighted]:text-destructive"
                                            @select="rowDelete"
                                        >
                                            <Trash2 class="h-4 w-4" />
                                            <span>Supprimer</span>
                                        </CommandItem>
                                    </CommandGroup>
                                </template>
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>
            </div>

            <!-- Column grip handle — horizontally centred on the hovered column -->
            <div
                v-if="colHandleLeft !== null || colMenuOpen"
                class="absolute z-30 -translate-x-1/2"
                :style="{ top: '-28px', left: (colHandleLeft ?? 0) + 'px' }"
                @mouseenter="cancelLeave"
            >
                <Popover v-model:open="colMenuOpen">
                    <PopoverTrigger as-child>
                        <button
                            class="flex h-6 w-6 rotate-90 items-center justify-center rounded text-muted-foreground/50 transition-colors hover:bg-muted hover:text-foreground"
                            title="Options de la colonne"
                            @click="onColHandleClick"
                        >
                            <GripVertical class="h-3.5 w-3.5" />
                        </button>
                    </PopoverTrigger>
                    <PopoverContent
                        align="start"
                        side="bottom"
                        class="w-56 p-0"
                    >
                        <Command>
                            <CommandInput
                                placeholder="Rechercher des actions…"
                            />
                            <CommandList>
                                <!-- Color mode -->
                                <template v-if="colColorMode">
                                    <div
                                        class="flex items-center gap-1 border-b border-border/40 px-2 py-1.5"
                                    >
                                        <button
                                            class="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                                            @click="colColorMode = false"
                                        >
                                            <ChevronLeft
                                                class="h-3 w-3"
                                            />Retour
                                        </button>
                                    </div>
                                    <div class="px-3 py-2">
                                        <p
                                            class="mb-2 text-[10px] font-semibold tracking-wider text-muted-foreground uppercase"
                                        >
                                            Couleur de fond
                                        </p>
                                        <div class="flex flex-wrap gap-2">
                                            <button
                                                v-for="c in cellColors"
                                                :key="c.value ?? 'none'"
                                                :class="[
                                                    'h-6 w-6 rounded-full border-2 border-transparent transition-all hover:scale-110 hover:border-foreground/40',
                                                    c.cls,
                                                ]"
                                                :title="c.label"
                                                @click="colSetColor(c.value)"
                                            />
                                        </div>
                                    </div>
                                </template>

                                <!-- Main menu -->
                                <template v-else>
                                    <CommandGroup>
                                        <CommandItem
                                            value="selectionner-col"
                                            @select="colSelect"
                                        >
                                            <MousePointer2 class="h-4 w-4" />
                                            <span>Sélectionner la colonne</span>
                                        </CommandItem>
                                    </CommandGroup>
                                    <CommandSeparator />
                                    <CommandGroup>
                                        <CommandItem
                                            value="col-gras"
                                            @select="() => colApplyMark('bold')"
                                        >
                                            <Bold class="h-4 w-4" />
                                            <span>Gras</span>
                                        </CommandItem>
                                        <CommandItem
                                            value="col-italique"
                                            @select="
                                                () => colApplyMark('italic')
                                            "
                                        >
                                            <Italic class="h-4 w-4" />
                                            <span>Italique</span>
                                        </CommandItem>
                                    </CommandGroup>
                                    <CommandSeparator />
                                    <CommandGroup>
                                        <CommandItem
                                            value="col-couleur"
                                            @select.prevent="
                                                colColorMode = true
                                            "
                                        >
                                            <Palette class="h-4 w-4" />
                                            <span>Couleur</span>
                                            <ChevronLeft
                                                class="ml-auto h-3 w-3 rotate-180 text-muted-foreground"
                                            />
                                        </CommandItem>
                                    </CommandGroup>
                                    <CommandSeparator />
                                    <CommandGroup>
                                        <CommandItem
                                            value="col-inserer-avant"
                                            @select="colInsertBefore"
                                        >
                                            <ArrowUp class="h-4 w-4" />
                                            <span>Insérer avant</span>
                                        </CommandItem>
                                        <CommandItem
                                            value="col-inserer-apres"
                                            @select="colInsertAfter"
                                        >
                                            <ArrowDown class="h-4 w-4" />
                                            <span>Insérer après</span>
                                        </CommandItem>
                                        <CommandItem
                                            value="col-dupliquer"
                                            @select="colDuplicate"
                                        >
                                            <Copy class="h-4 w-4" />
                                            <span>Dupliquer</span>
                                        </CommandItem>
                                    </CommandGroup>
                                    <CommandSeparator />
                                    <CommandGroup>
                                        <CommandItem
                                            value="col-effacer"
                                            @select="colClearContents"
                                        >
                                            <Eraser class="h-4 w-4" />
                                            <span>Effacer les contenus</span>
                                        </CommandItem>
                                        <CommandItem
                                            value="col-supprimer"
                                            class="text-destructive data-[highlighted]:text-destructive"
                                            @select="colDelete"
                                        >
                                            <Trash2 class="h-4 w-4" />
                                            <span>Supprimer</span>
                                        </CommandItem>
                                    </CommandGroup>
                                </template>
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>
            </div>

            <!-- Visual container keeps overflow-hidden for rounded corners on the table -->
            <div
                class="overflow-hidden rounded-lg border border-border/60 shadow-xs"
            >
                <div class="border-b border-border/40 bg-muted/30 px-3 py-1.5">
                    <span class="text-xs font-semibold text-muted-foreground"
                        >Tableau {{ tableNumber }}</span
                    >
                </div>
                <div ref="tableContentRef" class="overflow-x-auto">
                    <NodeViewContent class="editor-table-content" as="div" />
                </div>
            </div>
        </div>
    </NodeViewWrapper>
</template>
